import { atom, useRecoilState } from 'recoil';
import { useCallback, useEffect } from 'react';
import { PurchaseHistory } from 'ai-api/types/purchase';
import { PurchaseService } from '@/Data/PurchaseService';
import { useSession } from '@/Recoil/Session/Session';

interface PurchaseState {
    purchases: PurchaseHistory[];
    loading: boolean;
    error: string | null;
    lastFetch: number;
}

const purchaseState = atom<PurchaseState>({
    key: 'purchaseState',
    default: {
        purchases: [],
        loading: false,
        error: null,
        lastFetch: 0
    }
});

export const usePurchaseHistory = () => {
    const [state, setState] = useRecoilState(purchaseState);
    const { session } = useSession();

    const fetchPurchaseHistory = useCallback(async (forceRefresh = false) => {
        const now = Date.now();
        const cacheTime = 5 * 60 * 1000; // 5 minutes cache

        if (!forceRefresh && state.lastFetch && now - state.lastFetch < cacheTime) {
            return state.purchases;
        }

        if (!session?.userid) {
            setState(prev => ({ ...prev, purchases: [], loading: false, error: null }));
            return [];
        }

        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const response = await PurchaseService.getPurchaseHistory(undefined, 100, 0);
            if (response.code === 200 && response.data) {
                setState({
                    purchases: response.data,
                    loading: false,
                    error: null,
                    lastFetch: now
                });
                return response.data;
            }
            throw new Error('Failed to fetch purchase history');
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to fetch purchase history';
            setState(prev => ({ ...prev, loading: false, error: errorMessage }));
            return [];
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.userid, state.lastFetch, setState]);

    const hasProduct = useCallback((productId: string): boolean => {
        return state.purchases.some(
            purchase => purchase.productid === productId && purchase.status === 'succeeded'
        );
    }, [state.purchases]);

    useEffect(() => {
        if (session?.userid && state.purchases.length === 0 && !state.loading) {
            fetchPurchaseHistory();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.userid]);

    return {
        purchases: state.purchases,
        loading: state.loading,
        error: state.error,
        fetchPurchaseHistory,
        hasProduct
    };
};